import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api, { resolveScopedRestaurantId } from '../../utils/api';
import {
  BanknotesIcon,
  CreditCardIcon,
  ChartBarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

const PaymentsManagement = () => {
  const { user } = useContext(AuthContext);
  const [payments, setPayments] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [stats, setStats] = useState({
    totalPayments: 0,
    cashPayments: 0,
    onlinePayments: 0,
    cashAmount: 0,
    onlineAmount: 0,
    todayPayments: 0,
    todayAmount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, cash, online
  const [dateFilter, setDateFilter] = useState('today'); // all, today, week, month
  const [currentPage, setCurrentPage] = useState(1);
  const PAYMENTS_PER_PAGE = 10;

  useEffect(() => {
    fetchRestaurants();
  }, []);

  // Set default restaurant for staff users
  useEffect(() => {
    if (user) {
      const scopedRestaurantId = resolveScopedRestaurantId(user);

      if (user.role === 'staff' && user.restaurant_id) {
        setSelectedRestaurant(user.restaurant_id.toString());
        localStorage.setItem('selected_restaurant_id', user.restaurant_id.toString());
      } else if (user.role === 'admin') {
        const savedRestaurantId = localStorage.getItem('selected_restaurant_id');
        const initial = savedRestaurantId || (scopedRestaurantId ? scopedRestaurantId.toString() : '');
        setSelectedRestaurant(initial);
        if (initial) {
          localStorage.setItem('selected_restaurant_id', initial);
        }
      } else if (scopedRestaurantId) {
        setSelectedRestaurant(scopedRestaurantId.toString());
        localStorage.setItem('selected_restaurant_id', scopedRestaurantId.toString());
      }
    }
  }, [user]);

  useEffect(() => {
    if (selectedRestaurant && selectedRestaurant.trim()) {
      localStorage.setItem('selected_restaurant_id', selectedRestaurant.trim());
      return;
    }

    if (user?.role === 'admin') {
      localStorage.removeItem('selected_restaurant_id');
    }
  }, [selectedRestaurant, user]);

  // Fetch payments when restaurant filter changes
  useEffect(() => {
    if (restaurants.length > 0) {
      fetchPayments();
    }
  }, [selectedRestaurant, restaurants]);

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data.data || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      
      // Build params with restaurant filtering
      const params = { per_page: 10000 };
      const scopedRestaurantId = resolveScopedRestaurantId(user);
      
      // Staff: Always filter by their restaurant
      if (user?.role === 'staff' && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
        console.log('🏪 Staff filtering payments - restaurant_id:', user.restaurant_id);
      }
      // Admin: Use selectedRestaurant filter if set
      else if (selectedRestaurant && selectedRestaurant.trim()) {
        params.restaurant_id = selectedRestaurant.trim();
        console.log('👑 Admin filtering payments - restaurant_id:', selectedRestaurant);
      } else if (scopedRestaurantId) {
        params.restaurant_id = scopedRestaurantId;
        console.log('🌐 Scoped payments by helper - restaurant_id:', scopedRestaurantId);
      } else {
        console.log('🌐 No restaurant filter - showing all payments');
      }
      
      // Fetch dashboard data for accurate today's revenue
      const dashboardResponse = await api.get('/dashboard', { params });
      const dashboardData = dashboardResponse.data.data || {};
      
      // Request all orders with restaurant filter
      const response = await api.get('/orders', { params });
      
      let ordersData = response.data.data || response.data || [];
      if (ordersData && !Array.isArray(ordersData) && ordersData.data) {
        ordersData = ordersData.data;
      }

      const orders = Array.isArray(ordersData) ? ordersData : [];
      
      // Filter only completed orders for payments (include all non-cancelled orders)
      const completedOrders = orders.filter(order => 
        order.status === 'completed' || order.status === 'paid'
      );
      
      console.log('Total orders fetched:', orders.length);
      console.log('Completed orders:', completedOrders.length);
      
      // Calculate statistics (handle null/undefined payment methods, default to cash)
      const cashOrders = completedOrders.filter(o => 
        !o.payment_method || o.payment_method === 'cash' || o.payment_method === 'Cash'
      );
      const onlineOrders = completedOrders.filter(o => 
        o.payment_method === 'online' || o.payment_method === 'Online' || o.payment_method === 'gcash' || o.payment_method === 'GCash' || o.payment_method === 'Gcash'
      );
      
      const cashAmount = cashOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);
      const onlineAmount = onlineOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);
      
      // Today's payments - use dashboard data for accurate calculation
      const today = new Date().toISOString().split('T')[0];
      const todayOrders = completedOrders.filter(o => o.order_date?.startsWith(today));
      const todayAmount = dashboardData.todayRevenue || todayOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

      setStats({
        totalPayments: completedOrders.length,
        cashPayments: cashOrders.length,
        onlinePayments: onlineOrders.length,
        cashAmount,
        onlineAmount,
        todayPayments: todayOrders.length,
        todayAmount,
      });

      setPayments(completedOrders);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPayments = () => {
    let filtered = payments;

    // Filter by payment method (case-insensitive, handle null as cash)
    if (filter !== 'all') {
      filtered = filtered.filter(p => {
        const method = (p.payment_method || 'cash').toLowerCase();
        return method === filter.toLowerCase();
      });
    }

    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      
      if (dateFilter === 'today') {
        // Create a date string for today in YYYY-MM-DD format (local time)
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayString = `${year}-${month}-${day}`;
        
        filtered = filtered.filter(p => {
          if (!p.order_date) return false;
          // Parse the order date and get local date
          const orderDate = new Date(p.order_date);
          const orderYear = orderDate.getFullYear();
          const orderMonth = String(orderDate.getMonth() + 1).padStart(2, '0');
          const orderDay = String(orderDate.getDate()).padStart(2, '0');
          const orderDateString = `${orderYear}-${orderMonth}-${orderDay}`;
          return orderDateString === todayString;
        });
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => {
          if (!p.order_date) return false;
          const orderTime = new Date(p.order_date);
          return orderTime >= weekAgo;
        });
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(p => {
          if (!p.order_date) return false;
          const orderTime = new Date(p.order_date);
          return orderTime >= monthAgo;
        });
      }
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();
  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / PAYMENTS_PER_PAGE));
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * PAYMENTS_PER_PAGE,
    currentPage * PAYMENTS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, dateFilter, selectedRestaurant]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Calculate filtered stats
  const filteredCashPayments = filteredPayments.filter(p => 
    !p.payment_method || p.payment_method.toLowerCase() === 'cash'
  );
  const filteredOnlinePayments = filteredPayments.filter(p => 
    p.payment_method && p.payment_method.toLowerCase() !== 'cash'
  );
  const filteredCashAmount = filteredCashPayments.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0);
  const filteredOnlineAmount = filteredOnlinePayments.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0);
  const filteredTotalAmount = filteredCashAmount + filteredOnlineAmount;

  // Calculate percentage for charts
  const totalAmount = stats.cashAmount + stats.onlineAmount;
  const cashPercentage = totalAmount > 0 ? (stats.cashAmount / totalAmount) * 100 : 0;
  const onlinePercentage = totalAmount > 0 ? (stats.onlineAmount / totalAmount) * 100 : 0;
  
  // Filtered percentages
  const filteredCashPercentage = filteredTotalAmount > 0 ? (filteredCashAmount / filteredTotalAmount) * 100 : 0;
  const filteredOnlinePercentage = filteredTotalAmount > 0 ? (filteredOnlineAmount / filteredTotalAmount) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Payments Management</h2>
          {/* Show restaurant indicator */}
          {selectedRestaurant && restaurants.length > 0 && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
              {restaurants.find(r => r.restaurant_id.toString() === selectedRestaurant)?.name || 'Unknown Restaurant'}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs sm:text-sm text-gray-600">Track and analyze payment transactions</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Payments */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Payments</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{filteredPayments.length}</p>
            </div>
          </div>
        </div>

        {/* Cash Payments */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Cash Payments</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">₱{filteredCashAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{filteredCashPayments.length} txns</p>
            </div>
          </div>
        </div>

        {/* Online Payments */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
              <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Online Payments</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">₱{filteredOnlineAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{filteredOnlinePayments.length} txns</p>
            </div>
          </div>
        </div>

        {/* Today's Payments */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-2 sm:p-3 bg-amber-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">₱{filteredTotalAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{filteredPayments.length} txns</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Distribution Chart */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
          <span className="truncate">Payment Distribution</span>
        </h3>
        
        <div className="space-y-4">
          {/* Cash Bar */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between mb-1 gap-1">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Cash</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">
                ₱{filteredCashAmount.toLocaleString()} ({filteredCashPercentage.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
              <div
                className="bg-green-500 h-3 sm:h-4 rounded-full transition-all duration-500"
                style={{ width: `${filteredCashPercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{filteredCashPayments.length} txns</p>
          </div>

          {/* Online Bar */}
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between mb-1 gap-1">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Online Payment</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">
                ₱{filteredOnlineAmount.toLocaleString()} ({filteredOnlinePercentage.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
              <div
                className="bg-blue-500 h-3 sm:h-4 rounded-full transition-all duration-500"
                style={{ width: `${filteredOnlinePercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{filteredOnlinePayments.length} txns</p>
          </div>

          {/* Total */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-sm sm:text-base font-semibold text-gray-900">Total Revenue</span>
              <span className="text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">₱{filteredTotalAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-4">
          {/* Restaurant Filter - Only show for admin */}
          {user?.role === 'admin' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Restaurant</label>
              <select
                value={selectedRestaurant}
                onChange={(e) => setSelectedRestaurant(e.target.value)}
                className="w-full sm:w-64 rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-xs sm:text-sm"
              >
                <option value="">All Restaurants</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.restaurant_id} value={restaurant.restaurant_id}>
                    {restaurant.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Payment Method Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {['all', 'cash', 'online'].map((method) => (
                <button
                  key={method}
                  onClick={() => setFilter(method)}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                    filter === method
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {method === 'online' ? 'Online Payment' : method}
                </button>
              ))}
            </div>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Time Period</label>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {['all', 'today', 'week', 'month'].map((period) => (
                <button
                  key={period}
                  onClick={() => setDateFilter(period)}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                    dateFilter === period
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table/Cards */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Desktop/Tablet Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                {/* Show Restaurant column only for admin viewing all restaurants */}
                {user?.role === 'admin' && !selectedRestaurant && (
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Restaurant
                  </th>
                )}
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === 'admin' && !selectedRestaurant ? "6" : "5"} className="px-4 lg:px-6 py-12 text-center text-sm text-gray-500">
                    No payments found
                  </td>
                </tr>
              ) : (
                paginatedPayments.map((payment) => (
                  <tr key={payment.order_id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{payment.order_id}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.order_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    {/* Show Restaurant column only for admin viewing all restaurants */}
                    {user?.role === 'admin' && !selectedRestaurant && (
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {payment.restaurant?.name || `ID: ${payment.restaurant_id}`}
                        </span>
                      </td>
                    )}
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium gap-1 ${
                        !payment.payment_method || payment.payment_method.toLowerCase() === 'cash'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {!payment.payment_method || payment.payment_method.toLowerCase() === 'cash' ? (
                          <>
                            <BanknotesIcon className="w-4 h-4" />
                            Cash
                          </>
                        ) : (
                          <>
                            <CreditCardIcon className="w-4 h-4" />
                            Online
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ₱{parseFloat(payment.total_amount).toLocaleString('en-PH')}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="mr-1">✓</span> Paid
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200">
          {filteredPayments.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              No payments found
            </div>
          ) : (
            paginatedPayments.map((payment) => (
              <div key={payment.order_id} className="p-4 hover:bg-gray-50">
                <div className="space-y-3">
                  {/* Header with Order ID and Status */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Order #{payment.order_id}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(payment.order_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Paid
                    </span>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium gap-1 ${
                        !payment.payment_method || payment.payment_method.toLowerCase() === 'cash'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {!payment.payment_method || payment.payment_method.toLowerCase() === 'cash' ? (
                          <>
                            <BanknotesIcon className="w-4 h-4" />
                            Cash
                          </>
                        ) : (
                          <>
                            <CreditCardIcon className="w-4 h-4" />
                            Online Payment
                          </>
                        )}
                      </span>
                      {/* Show Restaurant badge for admin viewing all restaurants */}
                      {user?.role === 'admin' && !selectedRestaurant && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {payment.restaurant?.name || `ID: ${payment.restaurant_id}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Amount Paid</p>
                    <p className="text-xl font-bold text-gray-900">
                      ₱{parseFloat(payment.total_amount).toLocaleString('en-PH')}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {filteredPayments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-gray-600">
            Showing {(currentPage - 1) * PAYMENTS_PER_PAGE + 1}-
            {Math.min(currentPage * PAYMENTS_PER_PAGE, filteredPayments.length)} of {filteredPayments.length} payments
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-xs sm:text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsManagement;
